export type ExperimentFeatureValue = string | number | boolean | null;

/**
 * Tracks a map of growthbook experiment featureNames: featureValue
 * for the configured thread (or other variable in the future like KAID)
 */
let experimentFeatureStore: Record<string, ExperimentFeatureValue> = {};

/**
 * Set a single Experiment Feature value.
 * If the value is nullish, the variable will be deleted.
 */
export const setExperimentFeatureValue = (
    key: string,
    value?: ExperimentFeatureValue,
): void => {
    if (value == null) {
        delete experimentFeatureStore[key];
    } else {
        experimentFeatureStore[key] = value;
    }
};

/**
 * Set multiple Growthbook Experiment Feature values.
 *
 * The object passed to this method is combined with the existing variables,
 * overriding any existing variables with the same name. This does not clear
 * any variables.
 */
export const setExperimentFeatureValues = (
    variables: Record<string, ExperimentFeatureValue>,
): void => {
    experimentFeatureStore = {...experimentFeatureStore, ...variables};
};

/**
 * Get all current Growthbook experiment features and values
 * for the current context
 */
export const getExperimentFeatures = (): Record<
    string,
    ExperimentFeatureValue
> => ({
    ...experimentFeatureStore,
});

/**
 * Get the value for a single experiment feature flag.
 */
export const getExperimentFeatureValue = (
    key: string,
): ExperimentFeatureValue => experimentFeatureStore[key];

/**
 * Clear all currently set Growthbook Experiment Features
 *
 * An optional `options` object can be passed into the function to allow for
 * more control. The `options.exclude` config can be used to exempt specified
 * prompt variables from being cleared.
 */
export const clearExperimentFeatures = (options?: {
    exclude?: Array<string>;
}): void => {
    Object.keys(experimentFeatureStore).forEach((key) => {
        if (!options?.exclude?.includes(key)) {
            delete experimentFeatureStore[key];
        }
    });
};

/**
 * Clear a single Growthbook Experiment Feature by feature name
 */
export const clearExperimentFeature = (key: string): void => {
    delete experimentFeatureStore[key];
};
